import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { usePlayer, fmtTime } from "../context/PlayerContext";
import { Play, Pause, ListPlus, ArrowLeft, ExternalLink, Bookmark, Download, Users, Clock, Calendar, Feather } from "lucide-react";
import { toast } from "sonner";

export default function ItemDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { playTracks, addToQueue, queueMany, current, isPlaying, toggle, toggleSaved, isSaved } = usePlayer();

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.item(id)
      .then(d => alive && setData(d))
      .catch(() => alive && toast.error("Failed to load item"))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [id]);

  if (loading) return (
    <div className="animate-pulse space-y-6" data-testid="item-loading">
      <div className="h-8 w-40 bg-white/5 rounded" />
      <div className="flex gap-6">
        <div className="w-60 h-60 bg-white/5 rounded-lg" />
        <div className="flex-1 space-y-4 pt-6">
          <div className="h-5 w-24 bg-white/5 rounded" />
          <div className="h-10 w-2/3 bg-white/5 rounded" />
          <div className="h-4 w-1/2 bg-white/5 rounded" />
        </div>
      </div>
    </div>
  );

  if (!data) return (
    <div className="archival-card rounded-lg p-12 text-center" data-testid="item-not-found">
      <p className="font-display text-2xl">Item not found</p>
      <Link to="/explore" className="btn-ghost-gold inline-flex mt-4 items-center gap-2">
        <ArrowLeft className="w-4 h-4" /> Back to Explore
      </Link>
    </div>
  );

  const tracks = data.tracks.map(t => ({
    id: `${data.id}::${t.name}`,
    title: t.title,
    creator: data.creator,
    thumbnail: data.thumbnail,
    url: t.url,
    length: t.length,
    parent: data.id,
    parentTitle: data.title,
  }));

  const playAll = () => playTracks(tracks, 0);
  const playFrom = (idx) => playTracks(tracks, idx);
  const queueAll = () => { queueMany(tracks); };

  const totalDuration = data.tracks.reduce((a, b) => a + (b.length || 0), 0);
  const saved = isSaved(data.id);
  const isNowPlayingCard = current?.parent === data.id;

  return (
    <div className="animate-fade-up" data-testid="page-item">
      <Link to="/explore" className="inline-flex items-center gap-2 text-white/60 hover:text-emerald-soft mb-6" data-testid="back-link">
        <ArrowLeft className="w-4 h-4" /> Back to Explore
      </Link>

      {/* header */}
      <section className="grid md:grid-cols-[220px_1fr] gap-6 sm:gap-8 items-start">
        <div className="w-[140px] sm:w-full max-w-[220px] aspect-square rounded-lg overflow-hidden border border-white/8 bg-black/40 mx-auto md:mx-0 relative">
          <img
            src={data.thumbnail}
            alt=""
            className={`w-full h-full ${data.cover_fit === "contain" ? "object-contain" : "object-cover"}`}
          />
          <div className="absolute top-2 right-2 chip active">Archive</div>
        </div>

        <div>
          <p className="label-caps text-gold/85 mb-2">{data.series}</p>
          <h1 className="font-display text-2xl sm:text-3xl md:text-5xl leading-tight text-paper">{data.title}</h1>
          <p className="text-lg text-white/70 mt-3">{data.creator}</p>

          <div className="mt-5 flex flex-wrap items-center gap-4 text-sm text-white/55">
            {data.date && <Meta icon={<Calendar className="w-3.5 h-3.5" />}>{String(data.date).slice(0, 10)}</Meta>}
            <Meta icon={<Clock className="w-3.5 h-3.5" />}>{fmtTime(totalDuration)}</Meta>
            <Meta icon={<Users className="w-3.5 h-3.5" />}>{(data.downloads || 0).toLocaleString()} plays</Meta>
            <Meta icon={<Download className="w-3.5 h-3.5" />}>{data.tracks.length} track{data.tracks.length !== 1 && "s"}</Meta>
          </div>

          <div className="mt-7 flex flex-wrap items-center gap-3">
            <button
              onClick={isNowPlayingCard ? toggle : playAll}
              className="btn-primary inline-flex items-center gap-2"
              data-testid="item-play-all"
            >
              {isNowPlayingCard && isPlaying ? <Pause className="w-4 h-4 fill-current" /> : <Play className="w-4 h-4 fill-current" />}
              {isNowPlayingCard && isPlaying ? "Pause" : "Play all"}
            </button>
            <button onClick={queueAll} className="btn-ghost-gold inline-flex items-center gap-2" data-testid="item-queue-all">
              <ListPlus className="w-4 h-4" /> Queue all
            </button>
            <button
              onClick={() => toggleSaved(data)}
              className={`chip ${saved ? "active" : ""}`}
              data-testid="item-save"
            >
              <Bookmark className="w-4 h-4" /> {saved ? "Saved" : "Save"}
            </button>
            <a href={data.archive_url} target="_blank" rel="noreferrer" className="chip" data-testid="item-archive-link">
              <ExternalLink className="w-3.5 h-3.5" /> Archive.org
            </a>
          </div>

          {data.subject?.length > 0 && (
            <div className="mt-6 flex flex-wrap gap-2">
              {data.subject.slice(0, 8).map(s => (
                <Link key={s} to={`/explore?q=${encodeURIComponent(s)}`} className="chip" data-testid={`subject-${s}`}>{s}</Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {data.notes && (data.notes.summary || data.notes.highlights?.length > 0) && (
        <section className="mt-12" data-testid="dars-highlights">
          <div className="archival-card featured rounded-lg p-6 lg:p-8 relative overflow-hidden">
            <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-gold/[0.05] blur-3xl" />
            <div className="relative">
              <div className="flex items-center gap-2 mb-3">
                <Feather className="w-4 h-4 text-gold" />
                <span className="label-caps text-gold/90">Dars Highlights · Roman English</span>
              </div>
              {data.notes.summary && (
                <p className="font-display text-xl md:text-2xl leading-snug text-paper italic" data-testid="dars-summary">
                  &ldquo;{data.notes.summary}&rdquo;
                </p>
              )}
              {data.notes.highlights?.length > 0 && (
                <ul className="mt-6 space-y-3" data-testid="dars-highlight-list">
                  {data.notes.highlights.map((h, i) => (
                    <li key={i} className="flex gap-3 text-white/80 leading-relaxed">
                      <span className="mt-1 shrink-0 w-1.5 h-1.5 rounded-full bg-emerald-soft" />
                      <span>{h}</span>
                    </li>
                  ))}
                </ul>
              )}
              {data.notes.extra && (
                <p className="mt-5 text-white/60 text-sm leading-relaxed">{data.notes.extra}</p>
              )}
            </div>
          </div>
        </section>
      )}

      {data.description && (
        <section className="mt-14 max-w-3xl">
          <p className="label-caps text-gold/85 mb-3">About this recording</p>
          <div
            className="prose prose-invert prose-p:text-white/70 prose-p:leading-relaxed max-w-none text-white/70 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: data.description }}
          />
        </section>
      )}

      {/* tracklist */}
      <section className="mt-14">
        <div className="flex items-end justify-between mb-6">
          <div>
            <p className="label-caps text-gold/85">Track listing</p>
            <h2 className="font-display text-3xl text-paper mt-1">{data.tracks.length} tracks</h2>
          </div>
        </div>

        <div className="archival-card rounded-lg overflow-hidden" data-testid="tracklist">
          {data.tracks.map((t, i) => {
            const nowPlaying = current?.parent === data.id && current?.id === `${data.id}::${t.name}`;
            return (
              <div
                key={t.name}
                className={`flex items-center gap-3 md:gap-4 px-3 md:px-4 py-3 border-b border-white/5 last:border-0 hover:bg-white/[0.02] transition ${nowPlaying ? "bg-emerald-glow/10" : ""}`}
                data-testid={`track-row-${i}`}
              >
                <button
                  onClick={() => (nowPlaying ? toggle() : playFrom(i))}
                  className="play-btn shrink-0 !w-10 !h-10"
                  aria-label={nowPlaying && isPlaying ? "Pause" : "Play"}
                  data-testid={`track-play-${i}`}
                >
                  {nowPlaying && isPlaying
                    ? <Pause className="w-4 h-4 fill-current" />
                    : <Play className="w-4 h-4 ml-0.5 fill-current" />}
                </button>
                <div className="hidden sm:block w-8 text-white/40 tabular-nums font-display text-sm shrink-0">{String(i + 1).padStart(2, "0")}</div>
                <div className="flex-1 min-w-0">
                  <div className={`truncate font-display text-[15px] md:text-[17px] ${nowPlaying ? "text-emerald-soft" : "text-paper"}`}>{t.title}</div>
                  <div className="text-xs text-white/45 mt-0.5 truncate">{t.format} · {(t.size / (1024 * 1024)).toFixed(1)} MB</div>
                </div>
                <div className="hidden sm:block text-sm text-white/55 tabular-nums shrink-0">{fmtTime(t.length)}</div>
                <button
                  onClick={() => addToQueue({
                    id: `${data.id}::${t.name}`,
                    title: t.title,
                    creator: data.creator,
                    thumbnail: data.thumbnail,
                    url: t.url, length: t.length, parent: data.id, parentTitle: data.title,
                  })}
                  className="chip shrink-0"
                  aria-label="Add to queue"
                  data-testid={`track-queue-${i}`}
                >
                  <ListPlus className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Queue</span>
                </button>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}

function Meta({ icon, children }) {
  return <span className="inline-flex items-center gap-1.5">{icon}{children}</span>;
}
